import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Building2,
  HardHat,
  Package,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Users,
  MessageSquare,
  Smartphone,
  Globe,
  Zap,
  ClipboardCheck,
  FileText,
  Mail
} from "lucide-react";

const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";

// Tab Component
const TabButton = ({ active, onClick, icon: Icon, label, color }) => (
  <button
    onClick={onClick}
    className={`flex-1 flex items-center justify-center gap-2 py-4 px-6 font-semibold transition-all ${
      active 
        ? `bg-${color}-600 text-white shadow-lg` 
        : `bg-white text-slate-600 hover:bg-${color}-50 hover:text-${color}-700`
    } rounded-t-xl border-b-2 ${active ? `border-${color}-600` : 'border-transparent'}`}
  >
    <Icon className="w-5 h-5" />
    {label}
  </button>
);

// Step Card Component
const StepCard = ({ number, title, description, screenshot, readAlong, tips, color = "blue", navigationPath }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4 shadow-sm">
      <div className="flex items-start gap-4 p-6">
        <div className={`w-12 h-12 bg-${color}-600 text-white rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0`}>
          {number}
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-slate-800 text-lg mb-2">{title}</h4>
          <p className="text-slate-600 mb-4">{description}</p>
          
          {/* Navigation Path */}
          {navigationPath && (
            <div className={`bg-slate-800 rounded-lg p-4 mb-4`}>
              <div className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">
                <Globe className="w-4 h-4 text-cyan-400" />
                Navigation Path
              </div>
              <div className="flex items-center gap-2 text-white font-mono text-sm flex-wrap">
                {navigationPath.map((step, idx) => (
                  <span key={idx} className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded ${idx === navigationPath.length - 1 ? 'bg-cyan-600' : 'bg-slate-700'}`}>
                      {step}
                    </span>
                    {idx < navigationPath.length - 1 && <ChevronRight className="w-4 h-4 text-slate-500" />}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Read Along Script */}
          {readAlong && (
            <div className={`bg-${color}-50 border-l-4 border-${color}-500 p-4 rounded-r-lg mb-4`}>
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                <MessageSquare className={`w-4 h-4 text-${color}-600`} />
                Read Along Script
              </div>
              <p className="text-slate-700 italic">&quot;{readAlong}&quot;</p>
            </div>
          )}
          
          {/* Tips */}
          {tips && (
            <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 p-3 rounded-lg">
              <Zap className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span><strong>Pro Tip:</strong> {tips}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Checklist Component
const Checklist = ({ title, items, color = "blue" }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
    <h3 className={`font-semibold text-slate-800 mb-4 flex items-center gap-2`}>
      <ClipboardCheck className={`w-5 h-5 text-${color}-600`} />
      {title}
    </h3>
    <div className="space-y-3">
      {items.map((item, idx) => (
        <label key={idx} className="flex items-start gap-3 cursor-pointer group">
          <input 
            type="checkbox" 
            className={`w-5 h-5 rounded border-slate-300 mt-0.5 text-${color}-600 focus:ring-${color}-500`}
          />
          <div>
            <span className="text-slate-700 group-hover:text-slate-900">{item.text}</span>
            {item.note && (
              <p className="text-xs text-slate-500 mt-1">{item.note}</p>
            )}
          </div>
        </label>
      ))}
    </div>
  </div>
);

export default function OnboardingPlaybookPage() {
  const [activeTab, setActiveTab] = useState("hospital");

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-16 h-16 rounded-xl" />
            <div>
              <h1 className="text-3xl font-bold">Onboarding Playbook</h1>
              <p className="text-blue-300">Step-by-step guides for hospitals, contractors, and suppliers</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-5xl mx-auto px-4 -mt-2">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab("hospital")}
            className={`flex-1 flex items-center justify-center gap-2 py-4 px-6 font-semibold transition-all rounded-t-xl ${
              activeTab === "hospital"
                ? "bg-emerald-600 text-white shadow-lg"
                : "bg-white text-slate-600 hover:bg-emerald-50"
            }`}
          >
            <Building2 className="w-5 h-5" />
            Hospital Onboarding
          </button>
          <button
            onClick={() => setActiveTab("contractor")}
            className={`flex-1 flex items-center justify-center gap-2 py-4 px-6 font-semibold transition-all rounded-t-xl ${
              activeTab === "contractor"
                ? "bg-blue-600 text-white shadow-lg"
                : "bg-white text-slate-600 hover:bg-blue-50"
            }`}
          >
            <HardHat className="w-5 h-5" />
            Contractor Onboarding
          </button>
          <button
            onClick={() => setActiveTab("supplier")}
            className={`flex-1 flex items-center justify-center gap-2 py-4 px-6 font-semibold transition-all rounded-t-xl ${
              activeTab === "supplier"
                ? "bg-violet-600 text-white shadow-lg"
                : "bg-white text-slate-600 hover:bg-violet-50"
            }`}
          >
            <Package className="w-5 h-5" />
            Supplier Onboarding
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        
        {/* HOSPITAL ONBOARDING */}
        {activeTab === "hospital" && (
          <div>
            {/* Overview */}
            <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200 mb-8">
              <h2 className="text-xl font-bold text-emerald-800 mb-2">Hospital Onboarding Overview</h2>
              <p className="text-slate-700 mb-4">
                Get your healthcare facility set up on AskIsaac in under 30 minutes. This guide walks through 
                account setup, Telegram integration, and placing your first request.
              </p>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-emerald-700">
                  <Clock className="w-4 h-4" />
                  <span>~30 minutes</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-700">
                  <Users className="w-4 h-4" />
                  <span>1-2 staff needed</span>
                </div>
              </div>
            </div>

            {/* Pre-Onboarding Checklist */}
            <Checklist 
              title="Pre-Onboarding Requirements"
              color="emerald"
              items={[
                { text: "Facility name and address", note: "For account setup" },
                { text: "Primary contact email", note: "Will receive login credentials" },
                { text: "Contact phone number", note: "For urgent notifications" },
                { text: "Department/cost center (optional)", note: "For internal tracking" },
                { text: "Telegram app installed on phone", note: "Download from App Store or Google Play" }
              ]}
            />

            {/* Steps */}
            <h3 className="text-lg font-bold text-slate-800 mb-4">Onboarding Steps</h3>
            
            <StepCard 
              number={1}
              title="Account Creation"
              description="Admin creates hospital account in the AskIsaac admin portal with facility details and primary contact."
              navigationPath={["Admin Portal", "Hospitals Tab", "Add Health System"]}
              readAlong="First, we'll create your facility account. I just need your facility name, address, and a primary contact email. This email will receive the login credentials."
              tips="Always use a shared inbox or department email, not personal email, so multiple staff can access."
              color="emerald"
            />

            <StepCard 
              number={2}
              title="Welcome Email & Portal Access"
              description="Hospital contact receives welcome email with login link and temporary password. The email contains a secure link to set up their account."
              navigationPath={["Email Inbox", "Welcome to AskIsaac", "Click Login Link"]}
              readAlong="Check your email — you should have received a welcome message with your login credentials. Click the link to access your hospital portal."
              tips="If email doesn't arrive within 5 minutes, check spam folder or resend from admin panel."
              color="emerald"
            />

            <StepCard 
              number={3}
              title="Connect Telegram"
              description="Most important step! Connect Telegram to receive instant notifications and submit requests via text. This enables real-time communication."
              navigationPath={["Hospital Portal", "Profile Settings", "Connect Telegram", "Open AskIsaac Bot"]}
              readAlong="Now let's connect Telegram. This is how you'll get instant alerts and submit requests as easy as sending a text. Click 'Connect Telegram' and it will open the AskIsaac bot."
              tips="Hospital can use Telegram without logging into web portal — great for busy staff."
              color="emerald"
            />

            <StepCard 
              number={4}
              title="Test Request"
              description="Place a test backorder or maintenance request to verify everything is working. You can submit via web portal or Telegram."
              navigationPath={["Hospital Portal", "Submit Request"]}
              readAlong="Let's place a test request to make sure everything flows correctly. You can do this from the web portal or by typing /maintenance in Telegram. Try submitting a test maintenance request."
              tips="Use 'TEST' in the description so admin knows not to actually process it."
              color="emerald"
            />

            <StepCard 
              number={5}
              title="Assign Additional Staff (Optional)"
              description="Add more staff members who need access to submit or track requests. Each user can have their own Telegram connection."
              navigationPath={["Hospital Portal", "Team Management", "Invite Staff"]}
              readAlong="Would you like to add any other staff members? Each person can have their own login and Telegram connection for notifications."
              tips="Different staff can have different permission levels — view only, submit requests, or full admin."
              color="emerald"
            />

            {/* Post-Onboarding Checklist */}
            <Checklist 
              title="Post-Onboarding Verification"
              color="emerald"
              items={[
                { text: "Hospital can log into web portal" },
                { text: "Telegram bot is connected and responding" },
                { text: "Test request was received by admin" },
                { text: "Hospital received notification of test request status" },
                { text: "Walkthrough of tracking page completed" }
              ]}
            />
          </div>
        )}

        {/* CONTRACTOR ONBOARDING */}
        {activeTab === "contractor" && (
          <div>
            {/* Overview */}
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200 mb-8">
              <h2 className="text-xl font-bold text-blue-800 mb-2">Contractor Onboarding Overview</h2>
              <p className="text-slate-700 mb-4">
                Get contractors set up to receive job notifications, accept work, and update status in real-time. 
                This is critical for the live tracking feature.
              </p>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-blue-700">
                  <Clock className="w-4 h-4" />
                  <span>~20 minutes</span>
                </div>
                <div className="flex items-center gap-2 text-blue-700">
                  <Smartphone className="w-4 h-4" />
                  <span>Mobile phone required</span>
                </div>
              </div>
            </div>

            {/* Pre-Onboarding Checklist */}
            <Checklist 
              title="Pre-Onboarding Requirements"
              color="blue"
              items={[
                { text: "Contractor company name", note: "For invoicing and display" },
                { text: "Service types offered", note: "Plumbing, electrical, biomedical, etc." },
                { text: "Insurance certificate", note: "General liability + professional" },
                { text: "Primary contact email and phone" },
                { text: "Telegram app installed", note: "Required for job alerts and tracking" }
              ]}
            />

            {/* Steps */}
            <h3 className="text-lg font-bold text-slate-800 mb-4">Onboarding Steps</h3>
            
            <StepCard 
              number={1}
              title="Create Contractor Profile"
              description="Admin creates contractor profile with company info, service types, and coverage area."
              navigationPath={["Admin Portal", "Contractors Tab", "Add Contractor"]}
              readAlong="I'm creating your contractor profile now. What services do you offer? And what geographic area do you cover?"
              tips="Be specific with service types — this determines which jobs get sent to them."
              color="blue"
            />

            <StepCard 
              number={2}
              title="Send Setup Invitation"
              description="Contractor receives email with unique setup link to complete their profile and set password."
              navigationPath={["Contractor Row", "Actions", "Send Invite Email"]}
              readAlong="Check your email for the setup link. This lets you complete your profile, set your password, and connect Telegram."
              tips="Setup link expires in 72 hours. Can resend from admin panel if needed."
              color="blue"
            />

            <StepCard 
              number={3}
              title="Connect Telegram (Critical)"
              description="This is the most important step. Telegram enables job alerts, status updates, and GPS tracking."
              navigationPath={["Contractor Portal", "Profile", "Connect Telegram", "Start Bot"]}
              readAlong="Now connect your Telegram account. This is how you'll receive job alerts instantly and update your status. When you're on-site, we can track your location to give hospitals real-time arrival updates."
              tips="Contractor should enable Telegram notifications and location sharing for best experience."
              color="blue"
            />

            <StepCard 
              number={4}
              title="Test Job Flow"
              description="Send a test job to contractor to verify they receive notification and can respond."
              navigationPath={["Admin Portal", "Maintenance", "Assign to Contractor"]}
              readAlong="I'm sending you a test job now. You should get a Telegram notification. Try accepting it, then use /arrived when you would arrive on-site."
              tips="Walk through the full flow: accept → arrived → update → complete"
              color="blue"
            />

            <StepCard 
              number={5}
              title="Review Telegram Commands"
              description="Quick walkthrough of essential Telegram commands for managing jobs."
              navigationPath={["Telegram", "AskIsaac Bot", "/help"]}
              readAlong="Here are the commands you'll use most: /jobs shows your active jobs, /arrived marks you on-site, /update sends progress, and /complete finishes the job."
              tips="Print the command cheat sheet for their reference."
              color="blue"
            />

            {/* Post-Onboarding Checklist */}
            <Checklist 
              title="Post-Onboarding Verification"
              color="blue"
              items={[
                { text: "Contractor profile shows in admin list" },
                { text: "Telegram bot connected and responding" },
                { text: "Test job notification received" },
                { text: "Contractor can accept/decline jobs" },
                { text: "/arrived command sends location update" },
                { text: "Contractor understands billing/payment process" }
              ]}
            />
          </div>
        )}

        {/* SUPPLIER ONBOARDING */}
        {activeTab === "supplier" && (
          <div>
            {/* Overview */}
            <div className="bg-violet-50 rounded-xl p-6 border border-violet-200 mb-8">
              <h2 className="text-xl font-bold text-violet-800 mb-2">Supplier Onboarding Overview</h2>
              <p className="text-slate-700 mb-4">
                Get suppliers set up to receive RFQ notifications, submit quotes via portal or Telegram, 
                and participate in QuickBid competitive bidding.
              </p>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-violet-700">
                  <Clock className="w-4 h-4" />
                  <span>~20 minutes</span>
                </div>
                <div className="flex items-center gap-2 text-violet-700">
                  <Package className="w-4 h-4" />
                  <span>Product catalog helpful</span>
                </div>
              </div>
            </div>

            {/* Pre-Onboarding Checklist */}
            <Checklist 
              title="Pre-Onboarding Requirements"
              color="violet"
              items={[
                { text: "Company name and legal entity", note: "For purchase orders" },
                { text: "Product categories supplied", note: "Medical supplies, equipment, etc." },
                { text: "Primary contact email", note: "For RFQ notifications" },
                { text: "W-9 form (optional)", note: "Required before first payment" },
                { text: "Capabilities statement (optional)", note: "For Vendor Showcase profile" }
              ]}
            />

            {/* Steps */}
            <h3 className="text-lg font-bold text-slate-800 mb-4">Onboarding Steps</h3>
            
            <StepCard 
              number={1}
              title="Create Supplier Profile"
              description="Admin creates supplier profile with company info, product categories, and contact details."
              navigationPath={["Admin Portal", "Suppliers Tab", "Add Supplier"]}
              readAlong="I'm setting up your supplier profile. What product categories do you supply? Do you have minimum order amounts or lead times I should know about?"
              tips="Accurate product categories ensure suppliers get relevant RFQs."
              color="violet"
            />

            <StepCard 
              number={2}
              title="Send Portal Invitation"
              description="Supplier receives email to complete their profile and access the supplier portal."
              navigationPath={["Supplier Row", "Actions", "Send Email Invite"]}
              readAlong="Check your email for the setup link. You'll create your password and can add additional information like certifications and a company description."
              tips="Encourage suppliers to complete their Vendor Showcase profile for visibility."
              color="violet"
            />

            <StepCard 
              number={3}
              title="Connect Telegram"
              description="Optional but recommended. Enables instant RFQ alerts and quick quote submission via /bid command."
              navigationPath={["Supplier Portal", "Profile Settings", "Connect Telegram"]}
              readAlong="I recommend connecting Telegram. You'll get instant alerts when we have an RFQ matching your products, and you can submit quotes right from your phone using /bid."
              tips="Suppliers who respond faster via Telegram win more bids."
              color="violet"
            />

            <StepCard 
              number={4}
              title="Set Up Staff Hierarchy (if applicable)"
              description="For supplier organizations with multiple team members, set up the Chain of Command."
              navigationPath={["Supplier Portal", "Team Management", "Invite Staff"]}
              readAlong="If you have a team, we can set up your internal hierarchy. You can invite Supervisors who assign jobs, Technicians who execute work, and Authorized Buyers who can approve quotes. Each role has specific permissions."
              tips="Chain of Command is optional but recommended for larger supplier organizations."
              color="violet"
            />

            <StepCard 
              number={5}
              title="Test RFQ Flow"
              description="Send a test RFQ to verify supplier receives notification and can submit a quote."
              navigationPath={["Admin Portal", "Backorders", "Create Request", "Request Quotes"]}
              readAlong="I'm sending you a test RFQ. You can respond via the web portal or Telegram. Try using /quotes to see pending RFQs, then /bid to submit."
              tips="Walk through both web and Telegram quote submission methods."
              color="violet"
            />

            <StepCard 
              number={6}
              title="Explain QuickBid (Competitive Bidding)"
              description="Show how QuickBid works for high-value items requiring 3+ quotes."
              navigationPath={["Admin Portal", "QuickBid", "View Active Bids"]}
              readAlong="For orders over $2,500, we use QuickBid — our competitive bidding system. Multiple suppliers are invited to bid, and the hospital sees all quotes side-by-side. Fastest and most competitive wins."
              tips="Emphasize that response time matters as much as price."
              color="violet"
            />

            <StepCard 
              number={7}
              title="Vendor Showcase Profile (Optional)"
              description="Help supplier complete their public profile for the Vendor Marketplace."
              navigationPath={["Supplier Portal", "Vendor Showcase", "Edit Profile"]}
              readAlong="Would you like to set up your Vendor Showcase profile? This is a public page where hospitals can browse your capabilities, certifications, and contact you directly for quotes."
              tips="Profiles with logos, certifications, and detailed descriptions get more inquiries."
              color="violet"
            />

            {/* Post-Onboarding Checklist */}
            <Checklist 
              title="Post-Onboarding Verification"
              color="violet"
              items={[
                { text: "Supplier profile shows in admin list" },
                { text: "Portal login working" },
                { text: "Telegram connected (if opted in)" },
                { text: "Staff hierarchy configured (if applicable)" },
                { text: "Test RFQ received and quote submitted" },
                { text: "Supplier understands QuickBid process" },
                { text: "Payment terms and W-9 discussed" },
                { text: "Vendor Showcase profile started (optional)" }
              ]}
            />
          </div>
        )}

        {/* Common Resources */}
        <div className="mt-8 bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-bold text-slate-800 mb-4">Quick Links & Resources</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Link to="/user-guides" className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <FileText className="w-8 h-8 text-blue-500" />
              <div>
                <p className="font-medium text-slate-800">User Guides</p>
                <p className="text-sm text-slate-500">Role-specific documentation</p>
              </div>
            </Link>
            <Link to="/telegram-walkthrough" className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <MessageSquare className="w-8 h-8 text-emerald-500" />
              <div>
                <p className="font-medium text-slate-800">Telegram Setup</p>
                <p className="text-sm text-slate-500">Bot connection guide</p>
              </div>
            </Link>
            <Link to="/admin/email-campaigns" className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <Mail className="w-8 h-8 text-violet-500" />
              <div>
                <p className="font-medium text-slate-800">Email Campaigns</p>
                <p className="text-sm text-slate-500">Send welcome emails</p>
              </div>
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
